/**
 * Turning what somebody typed into a number the server will accept.
 *
 * `Number('')` is nought and `Number('abc')` is NaN, and the difference matters because JSON has
 * no way to write NaN — `JSON.stringify` turns it into `null`. So a number box that is empty, or
 * holding a value that never loaded, does not fail here. It travels to the server as null and
 * fails there, in English, against a schema expecting a number: the teacher who opened the
 * settings screen was told "Поле «Попыток»: Expected number, received null" and lost what they
 * had typed.
 *
 * Nothing here guesses at intent. A box left empty gets the default the screen already names
 * beside it, and every form reloads from the server after saving, so whatever was stored is back
 * on screen a moment later rather than hidden.
 */

/**
 * What a box holds, when a blank one means "the usual value".
 *
 * An empty box is the fallback rather than nought, because Number('') is nought and nought is a
 * real setting: it would quietly turn "how many files" into "no files may be attached". Where
 * nought is the meaningful blank — attempts, where the screen says nought means no limit — the
 * caller passes nought as the fallback and gets it.
 */
export function numberOr(text: string, fallback: number): number {
  const trimmed = text.trim();
  if (trimmed === '') return fallback;

  const value = Number(trimmed);
  return Number.isFinite(value) ? value : fallback;
}

/** What a box holds, when a blank one means "no limit", and the server takes null for that. */
export function optionalNumber(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;

  const value = Number(trimmed);
  return Number.isFinite(value) ? value : null;
}
